import React, { useState, useEffect } from 'react'
import { 
  Save,
  RotateCcw,
  Video,
  Calendar,
  Target,
  Building,
  DollarSign,
  Plus,
  ChevronRight,
  Home,
  Users,
  MapPin,
  Phone,
  Mail,
  Globe,
  Tag,
  UserCheck,
  Clock,
  CheckCircle,
  Monitor,
  MapPin as LocationIcon,
  Users as TeamIcon,
  FileText,
  Link,
  HelpCircle
} from 'lucide-react'
import { Breadcrumb } from '../../components/Common'
import HelpModal from '../../components/HelpModal/HelpModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import autoIncrementService from '../../services/autoIncrementService'
import { demoSchedulingService } from '../../services/demoSchedulingService'
import { leadGenerationService } from '../../services/leadGenerationService'
import prospectMasterService from '../../services/prospectMasterService'

const DemoSchedulingForm = () => {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('demoInfo')
  const [isLoadingDemoCode, setIsLoadingDemoCode] = useState(false)
  
  const [formData, setFormData] = useState({
    // Demo Information
    demoCode: '',
    demoTitle: '',
    demoDescription: '',
    demoObjectives: '',
    demoAgenda: '',
    demoDate: '',
    demoTime: '',
    demoDuration: 60,
    demoType: 'Online',
    demoStatus: 'Scheduled',
    priority: 'Medium',
    // Master Links
    lead: '',
    prospect: '',
    // Technical Details
    demoEnvironment: 'Production',
    demoVersion: '',
    demoFeatures: [],
    demoModules: [],
    demoData: '',
    // Location and Contact
    demoLocation: '',
    demoAddress: {
      street: '',
      city: '',
      state: '',
      country: '',
      zipCode: ''
    },
    demoVenue: 'Office',
    contactPerson: '',
    contactEmail: '',
    contactPhone: '',
    // Online Demo Details
    meetingLink: '',
    meetingId: '',
    meetingPassword: '',
    platform: 'Zoom',
    // Team and Resources
    demoPresenter: '',
    demoSupport: [],
    demoResources: [],
    // Follow-up Information
    followUpRequired: false,
    followUpDate: '',
    followUpNotes: '',
    nextSteps: '',
    decisionTimeline: '',
    budgetApproved: false,
    budgetAmount: 0
  })

  // Master data for dropdowns
  const [leads, setLeads] = useState([])
  const [prospects, setProspects] = useState([])
  const [users, setUsers] = useState([]) // Assuming a user service will be available

  // Load master data and demo code on component mount
  useEffect(() => {
    loadDemoCode()
    loadMasterData()
  }, [])

  const loadDemoCode = async () => {
    try {
      setIsLoadingDemoCode(true)
      const response = await autoIncrementService.getCurrentValue('DEMO-SCHED')
      if (response.success) {
        const sequence = response.data.formattedValue
        setFormData(prev => ({
          ...prev,
          demoCode: sequence
        }))
      } else {
        // If sequence doesn't exist, show a placeholder
        setFormData(prev => ({
          ...prev,
          demoCode: 'DS-0001' // Default placeholder
        }))
        console.warn('Auto-increment sequence DEMO-SCHED not found, using placeholder')
      }
    } catch (error) {
      console.error('Error loading demo code:', error)
      // Set a placeholder code instead of showing error
      setFormData(prev => ({
        ...prev,
        demoCode: 'DS-0001' // Default placeholder
      }))
      console.warn('Using placeholder demo code due to auto-increment error')
    } finally {
      setIsLoadingDemoCode(false)
    }
  }

  const loadMasterData = async () => {
    try {
      // Load leads
      const leadsResponse = await leadGenerationService.getAllLeads({ limit: 1000 })
      if (leadsResponse.success) {
        setLeads(leadsResponse.data.leads || [])
      } else {
        setLeads([])
        console.warn('Failed to load leads:', leadsResponse.message)
      }

      // Load prospects
      const prospectsResponse = await prospectMasterService.getAllProspects({ limit: 1000 })
      if (prospectsResponse.success) {
        setProspects(prospectsResponse.data.prospects || [])
      } else {
        setProspects([])
        console.warn('Failed to load prospects:', prospectsResponse.message)
      }

      // Load users (you might need to create a user service)
      // const usersResponse = await userService.getAllUsers()
      // if (usersResponse.success) {
      //   setUsers(usersResponse.data.users)
      // }
    } catch (error) {
      console.error('Error loading master data:', error)
      setLeads([])
      setProspects([])
      toast.error('Failed to load master data')
    }
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleNestedInputChange = (parentField, childField, value) => {
    setFormData(prev => ({
      ...prev,
      [parentField]: {
        ...prev[parentField],
        [childField]: value
      }
    }))
  }

  const handleArrayChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], value]
    }))
  }

  const handleArrayRemove = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    try {
      const demoData = {
        ...formData
      }

      const response = await demoSchedulingService.createDemo(demoData)
      if (response.success) {
        toast.success('Demo scheduling record created successfully')
        navigate('/crm/demo-scheduling')
      } else {
        toast.error(response.message || 'Failed to create demo scheduling record')
      }
    } catch (error) {
      console.error('Error creating demo scheduling record:', error)
      toast.error('Failed to create demo scheduling record')
    }
  }

  const handleReset = () => {
    setFormData({
      demoCode: '',
      demoTitle: '',
      demoDescription: '',
      demoObjectives: '',
      demoAgenda: '',
      demoDate: '',
      demoTime: '',
      demoDuration: 60,
      demoType: 'Online',
      demoStatus: 'Scheduled',
      priority: 'Medium',
      lead: '',
      prospect: '',
      demoEnvironment: 'Production',
      demoVersion: '',
      demoFeatures: [],
      demoModules: [],
      demoData: '',
      demoLocation: '',
      demoAddress: {
        street: '',
        city: '',
        state: '',
        country: '',
        zipCode: ''
      },
      demoVenue: 'Office',
      contactPerson: '',
      contactEmail: '',
      contactPhone: '',
      meetingLink: '',
      meetingId: '',
      meetingPassword: '',
      platform: 'Zoom',
      demoPresenter: '',
      demoSupport: [],
      demoResources: [],
      followUpRequired: false,
      followUpDate: '',
      followUpNotes: '',
      nextSteps: '',
      decisionTimeline: '',
      budgetApproved: false,
      budgetAmount: 0
    })
    loadDemoCode() // Reload auto-increment code
  }

  const helpContent = (
    <>
      <h3>What is Demo Scheduling?</h3>
      <p>Demo Scheduling lets you <strong>schedule and manage product demonstrations</strong>—link to leads/prospects, set date/time/duration, type (e.g. Online), location or meeting link, and follow-up.</p>
      <h3>Tabs</h3>
      <p><strong>Demo Info</strong> – Demo code, title, description, objectives, agenda, date, time, duration, type, status, priority, lead, prospect, and technical/location details.</p>
      <p><strong>Online Demo</strong> – Meeting link, platform (e.g. Zoom), presenter and support.</p>
      <p><strong>Follow-up</strong> – Follow-up required/date/notes, next steps, decision timeline, budget.</p>
      <h3>How to use</h3>
      <p>Fill required fields and link to a lead or prospect. Use <strong>Save</strong> to create. Use <strong>Reset</strong> to clear or navigate back to cancel.</p>
    </>
  )

  return (
    <div className="sales-dashboard">
      <Breadcrumb
        items={[
          { label: 'Home', path: '/dashboard' },
          { label: 'CRM', path: '/crm' },
          { label: 'Demo Scheduling', path: '/crm/demo-scheduling' },
          { label: 'Create', isCurrent: true }
        ]}
        rightSlot={
          <button type="button" className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1" onClick={() => setShowHelp(true)} title="Help" aria-label="Open help">
            <HelpCircle size={18} />
            <span className="d-none d-sm-inline">Help</span>
          </button>
        }
      />
      
      <div className="page-header">
        <div className="page-header-content">
          <div className="page-title">
            <h1>Demo Scheduling</h1>
            <p>Schedule and manage product demonstrations</p>
          </div>
        </div>
      </div>

      <div className="form-container">
        <div className="sac-form-header">
          <h2>Demo Scheduling ( Create )</h2>
        </div>
      
        {/* Tab Navigation */}
        <div className="tab-navigation">
          <button
            className={`tab-button ${activeTab === 'demoInfo' ? 'active' : ''}`}
            onClick={() => setActiveTab('demoInfo')}
          >
            <Video size={18} />
            <span>Demo Info</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'technical' ? 'active' : ''}`}
            onClick={() => setActiveTab('technical')}
          >
            <Monitor size={18} />
            <span>Technical</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'location' ? 'active' : ''}`}
            onClick={() => setActiveTab('location')}
          >
            <LocationIcon size={18} />
            <span>Location</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'team' ? 'active' : ''}`}
            onClick={() => setActiveTab('team')}
          >
            <TeamIcon size={18} />
            <span>Team</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'followUp' ? 'active' : ''}`}
            onClick={() => setActiveTab('followUp')}
          >
            <Calendar size={18} />
            <span>Follow-up</span>
          </button>
        </div>
          
        <form className="form-content" onSubmit={handleSubmit}>

          {/* Demo Information Tab */}
          {activeTab === 'demoInfo' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Demo Information</h3>
              </div>
              
              {/* Row 1: Demo Code, Title, Date, Time */}
              <div className="grid-row-4">
                <div className="form-group">
                  <label htmlFor="demoCode" className="form-label required">
                    Demo Code
                  </label>
                  <input
                    type="text"
                    id="demoCode"
                    name="demoCode"
                    value={isLoadingDemoCode ? 'Generating...' : formData.demoCode}
                    readOnly
                    className="form-input"
                    placeholder="Enter demo code"
                    style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' }}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="demoTitle" className="form-label required">
                    Demo Title
                  </label>
                  <input
                    type="text"
                    id="demoTitle"
                    name="demoTitle"
                    value={formData.demoTitle}
                    onChange={(e) => handleInputChange('demoTitle', e.target.value)}
                    className="form-input"
                    placeholder="Enter demo title"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="demoDate" className="form-label required">
                    Demo Date
                  </label>
                  <input
                    type="date"
                    id="demoDate"
                    name="demoDate"
                    value={formData.demoDate}
                    onChange={(e) => handleInputChange('demoDate', e.target.value)}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="demoTime" className="form-label required">
                    Demo Time
                  </label>
                  <input
                    type="time"
                    id="demoTime"
                    name="demoTime"
                    value={formData.demoTime}
                    onChange={(e) => handleInputChange('demoTime', e.target.value)}
                    className="form-input"
                  />
                </div>
              </div>

              {/* Row 2: Duration, Type, Status, Priority */}
              <div className="grid-row-4">
                <div className="form-group">
                  <label htmlFor="demoDuration" className="form-label required">
                    Duration (minutes)
                  </label>
                  <input
                    type="number"
                    id="demoDuration"
                    name="demoDuration"
                    value={formData.demoDuration}
                    onChange={(e) => handleInputChange('demoDuration', parseInt(e.target.value) || 60)}
                    className="form-input"
                    min="15"
                    max="480"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="demoType" className="form-label required">
                    Demo Type
                  </label>
                  <select
                    id="demoType"
                    name="demoType"
                    value={formData.demoType}
                    onChange={(e) => handleInputChange('demoType', e.target.value)}
                    className="form-input"
                  >
                    <option value="Online">Online</option>
                    <option value="In-Person">In-Person</option>
                    <option value="Recorded">Recorded</option>
                    <option value="Live Demo">Live Demo</option>
                    <option value="POC">POC</option>
                    <option value="Webinar">Webinar</option>
                    <option value="Workshop">Workshop</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="demoStatus" className="form-label required">
                    Demo Status
                  </label>
                  <select
                    id="demoStatus"
                    name="demoStatus"
                    value={formData.demoStatus}
                    onChange={(e) => handleInputChange('demoStatus', e.target.value)}
                    className="form-input"
                  >
                    <option value="Scheduled">Scheduled</option>
                    <option value="Confirmed">Confirmed</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Completed">Completed</option>
                    <option value="Cancelled">Cancelled</option>
                    <option value="Rescheduled">Rescheduled</option>
                    <option value="No Show">No Show</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="priority" className="form-label required">
                    Priority
                  </label>
                  <select
                    id="priority"
                    name="priority"
                    value={formData.priority}
                    onChange={(e) => handleInputChange('priority', e.target.value)}
                    className="form-input"
                  >
                    <option value="High">High</option>
                    <option value="Medium">Medium</option>
                    <option value="Low">Low</option>
                  </select>
                </div>
              </div>

              {/* Row 3: Lead and Prospect Selection */}
              <div className="grid-row-2">
                <div className="form-group">
                  <label htmlFor="lead" className="form-label required">
                    Select Lead
                  </label>
                  <select
                    id="lead"
                    name="lead"
                    value={formData.lead}
                    onChange={(e) => handleInputChange('lead', e.target.value)}
                    className="form-input"
                  >
                    <option value="">Select Lead</option>
                    {leads && leads.length > 0 ? leads.map(lead => (
                      <option key={lead._id} value={lead._id}>
                        {lead.leadCode} - {lead.prospect?.prospectName || 'N/A'}
                      </option>
                    )) : (
                      <option value="" disabled>No leads available</option>
                    )}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="prospect" className="form-label required">
                    Select Prospect
                  </label>
                  <select
                    id="prospect"
                    name="prospect"
                    value={formData.prospect}
                    onChange={(e) => handleInputChange('prospect', e.target.value)}
                    className="form-input"
                  >
                    <option value="">Select Prospect</option>
                    {prospects && prospects.length > 0 ? prospects.map(prospect => (
                      <option key={prospect._id} value={prospect._id}>
                        {prospect.prospectName} - {prospect.companyName}
                      </option>
                    )) : (
                      <option value="" disabled>No prospects available</option>
                    )}
                  </select>
                </div>
              </div>

              {/* Row 4: Description and Objectives */}
              <div className="grid-row-2">
                <div className="form-group">
                  <label htmlFor="demoDescription" className="form-label required">
                    Demo Description
                  </label>
                  <textarea
                    id="demoDescription"
                    name="demoDescription"
                    value={formData.demoDescription}
                    onChange={(e) => handleInputChange('demoDescription', e.target.value)}
                    className="form-input"
                    rows="3"
                    placeholder="Enter demo description"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="demoObjectives" className="form-label">
                    Demo Objectives
                  </label>
                  <textarea
                    id="demoObjectives"
                    name="demoObjectives"
                    value={formData.demoObjectives}
                    onChange={(e) => handleInputChange('demoObjectives', e.target.value)}
                    className="form-input"
                    rows="3"
                    placeholder="Enter demo objectives"
                  />
                </div>
              </div>

              {/* Row 5: Demo Agenda */}
              <div className="grid-row-1">
                <div className="form-group">
                  <label htmlFor="demoAgenda" className="form-label">
                    Demo Agenda
                  </label>
                  <textarea
                    id="demoAgenda"
                    name="demoAgenda"
                    value={formData.demoAgenda}
                    onChange={(e) => handleInputChange('demoAgenda', e.target.value)}
                    className="form-input"
                    rows="4"
                    placeholder="Enter demo agenda"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Technical Details Tab */}
          {activeTab === 'technical' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Technical Details</h3>
              </div>
              
              {/* Row 1: Environment, Version, Data */}
              <div className="grid-row-3">
                <div className="form-group">
                  <label htmlFor="demoEnvironment" className="form-label">
                    Demo Environment
                  </label>
                  <select
                    id="demoEnvironment"
                    name="demoEnvironment"
                    value={formData.demoEnvironment}
                    onChange={(e) => handleInputChange('demoEnvironment', e.target.value)}
                    className="form-input"
                  >
                    <option value="Production">Production</option>
                    <option value="Staging">Staging</option>
                    <option value="Development">Development</option>
                    <option value="Sandbox">Sandbox</option>
                    <option value="Local">Local</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="demoVersion" className="form-label">
                    Demo Version
                  </label>
                  <input
                    type="text"
                    id="demoVersion"
                    name="demoVersion"
                    value={formData.demoVersion}
                    onChange={(e) => handleInputChange('demoVersion', e.target.value)}
                    className="form-input"
                    placeholder="e.g., v2.1.0"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="demoData" className="form-label">
                    Demo Data
                  </label>
                  <input
                    type="text"
                    id="demoData"
                    name="demoData"
                    value={formData.demoData}
                    onChange={(e) => handleInputChange('demoData', e.target.value)}
                    className="form-input"
                    placeholder="Enter demo data details"
                  />
                </div>
              </div>

              {/* Row 2: Features and Modules */}
              <div className="grid-row-2">
                <div className="form-group">
                  <label htmlFor="demoFeatures" className="form-label">
                    Demo Features
                  </label>
                  <textarea
                    id="demoFeatures"
                    name="demoFeatures"
                    value={formData.demoFeatures.join(', ')}
                    onChange={(e) => handleInputChange('demoFeatures', e.target.value.split(',').map(f => f.trim()).filter(f => f))}
                    className="form-input"
                    rows="3"
                    placeholder="Enter demo features (comma separated)"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="demoModules" className="form-label">
                    Demo Modules
                  </label>
                  <textarea
                    id="demoModules"
                    name="demoModules"
                    value={formData.demoModules.join(', ')}
                    onChange={(e) => handleInputChange('demoModules', e.target.value.split(',').map(m => m.trim()).filter(m => m))}
                    className="form-input"
                    rows="3"
                    placeholder="Enter demo modules (comma separated)"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Location Tab */}
          {activeTab === 'location' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Location & Contact Information</h3>
              </div>
              
              {/* Row 1: Location, Venue, Contact Person */}
              <div className="grid-row-3">
                <div className="form-group">
                  <label htmlFor="demoLocation" className="form-label">
                    Demo Location
                  </label>
                  <input
                    type="text"
                    id="demoLocation"
                    name="demoLocation"
                    value={formData.demoLocation}
                    onChange={(e) => handleInputChange('demoLocation', e.target.value)}
                    className="form-input"
                    placeholder="Enter demo location"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="demoVenue" className="form-label">
                    Demo Venue
                  </label>
                  <select
                    id="demoVenue"
                    name="demoVenue"
                    value={formData.demoVenue}
                    onChange={(e) => handleInputChange('demoVenue', e.target.value)}
                    className="form-input"
                  >
                    <option value="Office">Office</option>
                    <option value="Client Site">Client Site</option>
                    <option value="Conference Room">Conference Room</option>
                    <option value="Virtual">Virtual</option>
                    <option value="Training Center">Training Center</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="contactPerson" className="form-label required">
                    Contact Person
                  </label>
                  <input
                    type="text"
                    id="contactPerson"
                    name="contactPerson"
                    value={formData.contactPerson}
                    onChange={(e) => handleInputChange('contactPerson', e.target.value)}
                    className="form-input"
                    placeholder="Enter contact person name"
                  />
                </div>
              </div>

              {/* Row 2: Contact Email and Phone */}
              <div className="grid-row-2">
                <div className="form-group">
                  <label htmlFor="contactEmail" className="form-label">
                    Contact Email
                  </label>
                  <input
                    type="email"
                    id="contactEmail"
                    name="contactEmail"
                    value={formData.contactEmail}
                    onChange={(e) => handleInputChange('contactEmail', e.target.value)}
                    className="form-input"
                    placeholder="Enter contact email"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="contactPhone" className="form-label">
                    Contact Phone
                  </label>
                  <input
                    type="tel"
                    id="contactPhone"
                    name="contactPhone"
                    value={formData.contactPhone}
                    onChange={(e) => handleInputChange('contactPhone', e.target.value)}
                    className="form-input"
                    placeholder="Enter contact phone"
                  />
                </div>
              </div>

              {/* Row 3: Address Details */}
              <div className="grid-row-2">
                <div className="form-group">
                  <label htmlFor="street" className="form-label">
                    Street Address
                  </label>
                  <input
                    type="text"
                    id="street"
                    name="street"
                    value={formData.demoAddress.street}
                    onChange={(e) => handleNestedInputChange('demoAddress', 'street', e.target.value)}
                    className="form-input"
                    placeholder="Enter street address"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="city" className="form-label">
                    City
                  </label>
                  <input
                    type="text"
                    id="city"
                    name="city"
                    value={formData.demoAddress.city}
                    onChange={(e) => handleNestedInputChange('demoAddress', 'city', e.target.value)}
                    className="form-input"
                    placeholder="Enter city"
                  />
                </div>
              </div>

              {/* Row 4: State, Country, Zip */}
              <div className="grid-row-3">
                <div className="form-group">
                  <label htmlFor="state" className="form-label">
                    State
                  </label>
                  <input
                    type="text"
                    id="state"
                    name="state"
                    value={formData.demoAddress.state}
                    onChange={(e) => handleNestedInputChange('demoAddress', 'state', e.target.value)}
                    className="form-input"
                    placeholder="Enter state"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="country" className="form-label">
                    Country
                  </label>
                  <input
                    type="text"
                    id="country"
                    name="country"
                    value={formData.demoAddress.country}
                    onChange={(e) => handleNestedInputChange('demoAddress', 'country', e.target.value)}
                    className="form-input"
                    placeholder="Enter country"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="zipCode" className="form-label">
                    Zip Code
                  </label>
                  <input
                    type="text"
                    id="zipCode"
                    name="zipCode"
                    value={formData.demoAddress.zipCode}
                    onChange={(e) => handleNestedInputChange('demoAddress', 'zipCode', e.target.value)}
                    className="form-input"
                    placeholder="Enter zip code"
                  />
                </div>
              </div>

              {/* Row 5: Online Meeting Details */}
              {formData.demoType === 'Online' && (
                <div className="grid-row-2">
                  <div className="form-group">
                    <label htmlFor="meetingLink" className="form-label">
                      Meeting Link
                    </label>
                    <input
                      type="url"
                      id="meetingLink"
                      name="meetingLink"
                      value={formData.meetingLink}
                      onChange={(e) => handleInputChange('meetingLink', e.target.value)}
                      className="form-input"
                      placeholder="Enter meeting link"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="platform" className="form-label">
                      Platform
                    </label>
                    <select
                      id="platform"
                      name="platform"
                      value={formData.platform}
                      onChange={(e) => handleInputChange('platform', e.target.value)}
                      className="form-input"
                    >
                      <option value="Zoom">Zoom</option>
                      <option value="Teams">Teams</option>
                      <option value="Google Meet">Google Meet</option>
                      <option value="WebEx">WebEx</option>
                      <option value="Skype">Skype</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Team Tab */}
          {activeTab === 'team' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Team & Resources</h3>
              </div>
              
              {/* Row 1: Demo Presenter */}
              <div className="grid-row-1">
                <div className="form-group">
                  <label htmlFor="demoPresenter" className="form-label required">
                    Demo Presenter
                  </label>
                  <input
                    type="text" // This should ideally be a select from users
                    id="demoPresenter"
                    name="demoPresenter"
                    value={formData.demoPresenter}
                    onChange={(e) => handleInputChange('demoPresenter', e.target.value)}
                    className="form-input"
                    placeholder="Enter demo presenter name"
                  />
                </div>
              </div>

              {/* Row 2: Demo Support Team */}
              <div className="grid-row-1">
                <div className="form-group">
                  <label htmlFor="demoSupport" className="form-label">
                    Demo Support Team
                  </label>
                  <textarea
                    id="demoSupport"
                    name="demoSupport"
                    value={formData.demoSupport.join(', ')}
                    onChange={(e) => handleInputChange('demoSupport', e.target.value.split(',').map(s => s.trim()).filter(s => s))}
                    className="form-input"
                    rows="3"
                    placeholder="Enter support team members (comma separated)"
                  />
                </div>
              </div>

              {/* Row 3: Demo Resources */}
              <div className="grid-row-1">
                <div className="form-group">
                  <label htmlFor="demoResources" className="form-label">
                    Demo Resources
                  </label>
                  <textarea
                    id="demoResources"
                    name="demoResources"
                    value={formData.demoResources.join(', ')}
                    onChange={(e) => handleInputChange('demoResources', e.target.value.split(',').map(r => r.trim()).filter(r => r))}
                    className="form-input"
                    rows="3"
                    placeholder="Enter demo resources (comma separated)"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Follow-up Tab */}
          {activeTab === 'followUp' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Follow-up Information</h3>
              </div>
              
              {/* Row 1: Follow-up Required, Follow-up Date */}
              <div className="grid-row-2">
                <div className="form-group">
                  <label htmlFor="followUpRequired" className="form-label">
                    Follow-up Required
                  </label>
                  <select
                    id="followUpRequired"
                    name="followUpRequired"
                    value={formData.followUpRequired}
                    onChange={(e) => handleInputChange('followUpRequired', e.target.value === 'true')}
                    className="form-input"
                  >
                    <option value={false}>No</option>
                    <option value={true}>Yes</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="followUpDate" className="form-label">
                    Follow-up Date
                  </label>
                  <input
                    type="date"
                    id="followUpDate"
                    name="followUpDate"
                    value={formData.followUpDate}
                    onChange={(e) => handleInputChange('followUpDate', e.target.value)}
                    className="form-input"
                  />
                </div>
              </div>

              {/* Row 2: Next Steps, Decision Timeline */}
              <div className="grid-row-2">
                <div className="form-group">
                  <label htmlFor="nextSteps" className="form-label">
                    Next Steps
                  </label>
                  <textarea
                    id="nextSteps"
                    name="nextSteps"
                    value={formData.nextSteps}
                    onChange={(e) => handleInputChange('nextSteps', e.target.value)}
                    className="form-input"
                    rows="3"
                    placeholder="Enter next steps"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="decisionTimeline" className="form-label">
                    Decision Timeline
                  </label>
                  <input
                    type="text"
                    id="decisionTimeline"
                    name="decisionTimeline"
                    value={formData.decisionTimeline}
                    onChange={(e) => handleInputChange('decisionTimeline', e.target.value)}
                    className="form-input"
                    placeholder="e.g., 2 weeks, 1 month"
                  />
                </div>
              </div>

              {/* Row 3: Budget Information */}
              <div className="grid-row-2">
                <div className="form-group">
                  <label htmlFor="budgetApproved" className="form-label">
                    Budget Approved
                  </label>
                  <select
                    id="budgetApproved"
                    name="budgetApproved"
                    value={formData.budgetApproved}
                    onChange={(e) => handleInputChange('budgetApproved', e.target.value === 'true')}
                    className="form-input"
                  >
                    <option value={false}>No</option>
                    <option value={true}>Yes</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="budgetAmount" className="form-label">
                    Budget Amount
                  </label>
                  <input
                    type="number"
                    id="budgetAmount"
                    name="budgetAmount"
                    value={formData.budgetAmount}
                    onChange={(e) => handleInputChange('budgetAmount', parseFloat(e.target.value) || 0)}
                    className="form-input"
                    placeholder="Enter budget amount"
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>

              {/* Row 4: Follow-up Notes */}
              <div className="grid-row-1">
                <div className="form-group">
                  <label htmlFor="followUpNotes" className="form-label">
                    Follow-up Notes
                  </label>
                  <textarea
                    id="followUpNotes"
                    name="followUpNotes"
                    value={formData.followUpNotes}
                    onChange={(e) => handleInputChange('followUpNotes', e.target.value)}
                    className="form-input"
                    rows="4"
                    placeholder="Enter follow-up notes"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Form Actions */}
          <div className="form-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={handleReset}
            >
              <RotateCcw size={18} />
              Reset
            </button>
            <button
              type="submit"
              className="btn-primary"
            >
              <Save size={18} />
              Save
            </button>
          </div>
        </form>
      </div>

      <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} title="Demo Scheduling – Help">
        {helpContent}
      </HelpModal>
    </div>
  )
}

export default DemoSchedulingForm
